import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { subirArchivo, abrirArchivoSeguro } from "../utils/upload";
import { BadgeFormaPago } from "../utils/estados";
import {
  BadgeFase,
  BANCOS_PAGO,
  DesglosePago,
  TablaItemsPago,
  DocumentosRespaldo,
  folioPago,
  montoTextoPago,
  netoTextoPago,
  nombreMetodo,
  textoArticulos,
} from "../utils/pagosUi";
import { EncabezadoDetalle, tarjetaTema, tituloSeccionTema } from "../components/TemaDetalle";
import Cargando from "../components/Cargando";

const hoy = () => new Date().toISOString().slice(0, 10);

const textoFecha = (valor) => (valor ? new Date(valor).toLocaleString("es-NI") : "—");

// Qué pide cada forma de pago al registrarla. "requerido" son los campos que
// el servidor exige; el resto es evidencia opcional.
const CAMPOS = {
  cheque: { referencia: "Número de cheque", requerida: true, banco: true, comprobante: "opcional" },
  transferencia: { referencia: "Número de referencia", requerida: true, banco: true, comprobante: "obligatorio" },
  efectivo: { referencia: null, banco: false, comprobante: "opcional" },
  tarjeta: { referencia: "Referencia o autorización (opcional)", requerida: false, banco: false, comprobante: "obligatorio" },
};

// Pantalla 3 de 3 — Registrar pago: se ejecuta el pago y se deja la evidencia
// (número, banco, fecha, comprobante). Al guardar, el mismo pago se muestra
// como "Pago procesado" y compras recibe el aviso. Quien no puede pagar (o un
// pago ya hecho) ve solo el resultado.
function RegistrarPago() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const puedePagar = usuario?.rol === "pagos" || usuario?.rol === "administrador";

  const [pago, setPago] = useState(null);
  const [error, setError] = useState("");
  const [referencia, setReferencia] = useState("");
  const [banco, setBanco] = useState("");
  const [fecha, setFecha] = useState(hoy());
  const [observaciones, setObservaciones] = useState("");
  const [comprobante, setComprobante] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [recienRegistrado, setRecienRegistrado] = useState(false);
  const archivoRef = useRef(null);

  useEffect(() => {
    apiFetch(`/api/pagos/${id}`)
      .then(setPago)
      .catch((err) => setError(err.message));
  }, [id]);

  if (error && !pago) return <p style={{ color: "#a3402f" }}>{error}</p>;
  if (!pago) return <Cargando />;
  // Antes de registrar hay que gestionar (confirmar o cambiar el medio).
  if (pago.fase === "por_pagar") return <Navigate to={`/pagos/${pago.id}`} replace />;

  // Un pago en proceso solo se registra desde esta pantalla si se puede pagar;
  // compras (solo consulta) lo ve en Gestionar.
  if (pago.fase === "en_proceso" && !puedePagar) return <Navigate to={`/pagos/${pago.id}`} replace />;

  const procesado = pago.fase === "procesado";
  const campos = CAMPOS[pago.metodo] || CAMPOS.efectivo;
  const items = pago.items || [];

  const subirComprobante = async (file) => {
    if (!file) return;
    setSubiendo(true);
    setError("");
    try {
      setComprobante(await subirArchivo(file));
    } catch (err) {
      setError(err.message);
    } finally {
      setSubiendo(false);
    }
  };

  const registrar = async () => {
    if (campos.requerida && !referencia.trim()) return setError(`Escribe el ${campos.referencia.toLowerCase()}.`);
    if (campos.comprobante === "obligatorio" && !comprobante) return setError("Adjunta el comprobante del pago.");
    setGuardando(true);
    setError("");
    try {
      await apiFetch(`/api/pagos/${pago.id}/pagar`, {
        method: "POST",
        body: JSON.stringify({
          referencia: referencia.trim(),
          banco: banco.trim(),
          fecha_pago: fecha,
          observaciones: observaciones.trim(),
          comprobante,
        }),
      });
      setPago(await apiFetch(`/api/pagos/${pago.id}`));
      setRecienRegistrado(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  // Si se abrió desde otra pantalla (p. ej. el detalle de la solicitud), se regresa a ella;
  // si no, a la lista de Pagos.
  const destinoRegreso = location.state?.desde || "/pagos";

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <Link to={procesado ? destinoRegreso : `/pagos/${pago.id}`} style={styles.volver}>
        {procesado ? "← Regresar" : "← Gestionar pago"}
      </Link>
      <EncabezadoDetalle
        titulo={procesado ? "✓ Pago procesado" : "🧾 Registrar pago"}
        enlace={`/solicitud/${pago.solicitud_id}`}
        enlaceTexto={`Solicitud ${folioPago(pago.solicitud_id)}`}
        insignia={<BadgeFase fase={pago.fase} />}
        subtitulo={`${pago.proveedor_nombre} · ${textoArticulos(items)}`}
        etiquetaMonto={procesado ? "Pagado" : netoTextoPago(pago) ? "Neto a pagar" : "Por pagar"}
        monto={netoTextoPago(pago) || montoTextoPago(pago)}
        notaMonto={netoTextoPago(pago) ? `Factura: ${montoTextoPago(pago)}` : null}
        tono={procesado ? "pagado" : "proceso"}
      />

      {recienRegistrado && (
        <div style={styles.exito}>✓ Pago registrado. Compras fue notificada y ya puede continuar con la compra.</div>
      )}

      {/* Ya pagado, "Detalle del pago" dice la forma: esta tarjeta solo sirve si hubo cambio de medio. */}
      {(!procesado || pago.cambio_de_medio) && (
      <div style={styles.card}>
        <div style={styles.datos}>
          <Dato etiqueta="Forma de pago" valor={<BadgeFormaPago valor={pago.metodo} />} />
          {pago.cambio_de_medio && (
            <>
              <Dato etiqueta="Forma original (Compras)" valor={<BadgeFormaPago valor={pago.metodo_propuesto} />} />
              <Dato etiqueta="Cambio realizado por" valor={pago.cambiado_por_nombre || "Contabilidad"} />
              <Dato etiqueta="Motivo del cambio" valor={pago.motivo_cambio || "—"} completo />
            </>
          )}
        </div>
      </div>
      )}

      {procesado ? (
        <div style={styles.card}>
          <div style={styles.tituloSeccion}>Detalle del pago</div>
          <div style={styles.datos}>
            <Dato etiqueta="Forma propuesta por Compras" valor={nombreMetodo(pago.metodo_propuesto || pago.metodo)} />
            <Dato etiqueta="Forma utilizada" valor={nombreMetodo(pago.metodo)} />
            {pago.referencia && (
              <Dato
                etiqueta={pago.metodo === "cheque" ? "Número de cheque" : "Referencia"}
                valor={pago.referencia}
              />
            )}
            {pago.banco && <Dato etiqueta="Banco" valor={pago.banco} />}
            <Dato etiqueta="Fecha del pago" valor={pago.fecha_pago ? pago.fecha_pago.split("-").reverse().join("/") : "—"} />
            <Dato etiqueta="Registrado por" valor={pago.pagado_por_nombre || "—"} />
            <Dato etiqueta="Registrado el" valor={textoFecha(pago.pagado_en)} />
            <Dato
              etiqueta="Comprobante"
              valor={
                pago.comprobante ? (
                  <a href="#" onClick={(e) => abrirArchivoSeguro(e, pago.comprobante)} style={styles.link}>
                    🧾 Ver comprobante ✓
                  </a>
                ) : (
                  "Sin comprobante"
                )
              }
            />
            <Dato etiqueta="Compras notificada" valor="✓" />
          </div>
          {pago.observaciones && <p style={styles.observaciones}>Observaciones: {pago.observaciones}</p>}
          <div style={styles.acciones}>
            <button type="button" onClick={() => navigate(destinoRegreso)} style={styles.botonPrincipal}>
              Regresar
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.card}>
          <div style={styles.tituloSeccion}>Información del pago — {nombreMetodo(pago.metodo)}</div>

          <div style={styles.formulario}>
            {campos.referencia && (
              <label style={styles.etiqueta}>
                {campos.referencia}
                {campos.requerida ? " *" : ""}
                <input
                  type="text"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  placeholder={pago.metodo === "cheque" ? "Ej. CH-000458" : "Ej. TRX-45821"}
                  style={styles.input}
                />
              </label>
            )}
            {campos.banco && (
              <label style={styles.etiqueta}>
                Banco
                <select value={banco} onChange={(e) => setBanco(e.target.value)} style={styles.input}>
                  <option value="">Elegir banco…</option>
                  {BANCOS_PAGO.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {pago.metodo === "efectivo" && (
              <label style={styles.etiqueta}>
                Monto entregado
                <input type="text" value={netoTextoPago(pago) || montoTextoPago(pago)} readOnly style={{ ...styles.input, background: "#f4f6f8" }} />
              </label>
            )}
            <label style={styles.etiqueta}>
              Fecha del pago
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={styles.input} />
            </label>
          </div>

          <label style={{ ...styles.etiqueta, marginTop: "0.9rem" }}>
            Observaciones (opcional)
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={2}
              style={{ ...styles.input, resize: "vertical" }}
            />
          </label>

          <div style={{ marginTop: "0.9rem" }}>
            <input
              ref={archivoRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: "none" }}
              onChange={(e) => subirComprobante(e.target.files[0])}
            />
            <button type="button" onClick={() => archivoRef.current?.click()} style={styles.botonSecundario}>
              📎 {subiendo ? "Subiendo..." : comprobante ? "Comprobante adjuntado ✓" : "Adjuntar comprobante"}
              {!comprobante ? (campos.comprobante === "obligatorio" ? " (obligatorio)" : " (opcional)") : ""}
            </button>
          </div>

          {error && <p style={{ color: "#a3402f", fontSize: "0.85rem" }}>{error}</p>}

          <div style={styles.acciones}>
            <button type="button" onClick={() => navigate(`/pagos/${pago.id}`)} style={styles.botonSecundario}>
              Cancelar
            </button>
            <button type="button" disabled={guardando || subiendo} onClick={registrar} style={styles.botonPrincipal}>
              {guardando ? "Guardando..." : "✓ Registrar pago"}
            </button>
          </div>
        </div>
      )}

      <DocumentosRespaldo datos={pago} />

      <div style={styles.card}>
        <div style={{ ...styles.tituloSeccion, marginBottom: "0.7rem" }}>Productos incluidos</div>
        <TablaItemsPago items={items} moneda={pago.moneda} />
        <DesglosePago pago={pago} />
      </div>
    </div>
  );
}

// completo: ocupa toda la fila (para textos largos como el motivo).
function Dato({ etiqueta, valor, completo }) {
  return (
    <div style={completo ? { gridColumn: "1 / -1" } : undefined}>
      <div style={styles.etiquetaDato}>{etiqueta}</div>
      <div style={styles.valorDato}>{valor}</div>
    </div>
  );
}

const styles = {
  volver: { color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" },
  exito: { marginTop: "0.9rem", padding: "0.7rem 1rem", borderRadius: 10, background: "#dcf0e6", color: "#1e5e3f", fontWeight: 600, fontSize: "0.9rem" },
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.1rem 1.3rem",
    marginTop: "1rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    ...tarjetaTema,
  },
  datos: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "0.9rem 1.5rem",
  },
  etiquetaDato: { fontSize: "0.68rem", fontWeight: 700, color: "#8993a1", textTransform: "uppercase", letterSpacing: "0.02em", marginBottom: "0.15rem" },
  valorDato: { fontSize: "0.9rem", color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, overflowWrap: "anywhere" },
  link: { color: "var(--tema-primario, #2f6690)", textDecoration: "none", fontWeight: 600 },
  tituloSeccion: { ...tituloSeccionTema, marginBottom: "0.8rem" },
  formulario: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "0.9rem 1.2rem",
    marginTop: "0.8rem",
  },
  etiqueta: { display: "flex", flexDirection: "column", gap: "0.3rem", fontSize: "0.82rem", fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" },
  input: { padding: "0.5rem 0.6rem", border: "1px solid #d3d9df", borderRadius: 6, fontFamily: "inherit", fontSize: "0.88rem", boxSizing: "border-box", width: "100%" },
  observaciones: { margin: "0.9rem 0 0", fontSize: "0.86rem", color: "#3d4a5c" },
  acciones: { display: "flex", gap: "0.6rem", flexWrap: "wrap", justifyContent: "flex-end", marginTop: "1rem" },
  botonPrincipal: {
    background: "#0f7c8a",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.6rem 1.2rem",
    fontSize: "0.88rem",
    fontWeight: 700,
    cursor: "pointer",
  },
  botonSecundario: {
    background: "#fff",
    color: "var(--tema-titulo, #0b3c60)",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.55rem 1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default RegistrarPago;
